import React from 'react';
import { AssessmentPoint } from '../../types';
import { CandidateEvaluation, DEGREE_LEVELS, getStatusBadgeClass } from './evaluationTypes';

interface GroupedCriteria {
  category: string;
  points: AssessmentPoint[];
}

interface EvaluationDetailsProps {
  evaluation: CandidateEvaluation;
  groupedCriteria: GroupedCriteria[];
  onPrintAssessmentSheet: () => void;
  onClose: () => void;
}

const EvaluationDetails: React.FC<EvaluationDetailsProps> = ({
  evaluation, groupedCriteria, onPrintAssessmentSheet, onClose,
}) => {
  return (
    <div className="card">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Evaluation Details</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      <div className="p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div>
            <label className="text-sm font-medium text-gray-500">Candidate</label>
            <p className="mt-1 text-lg font-semibold text-gray-900">{evaluation.candidateName}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">Job Title</label>
            <p className="mt-1 text-lg font-semibold text-gray-900">{evaluation.jobTitle}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">Evaluation Date</label>
            <p className="mt-1 text-lg font-semibold text-gray-900">
              {new Date(evaluation.evaluationDate).toLocaleDateString()}
            </p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">Evaluator</label>
            <p className="mt-1 text-lg font-semibold text-gray-900">{evaluation.evaluatorName}</p>
          </div>
        </div>

        <div className="bg-gray-50 p-6 rounded-lg">
          <div className="text-center">
            <div className="text-4xl font-bold text-gray-900 mb-2">
              {evaluation.percentage.toFixed(1)}%
            </div>
            <div className="text-sm text-gray-500 mb-4">
              {evaluation.totalScore} out of {evaluation.maxScore} points
            </div>
            <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium ${getStatusBadgeClass(evaluation.status)}`}>
              {evaluation.status.charAt(0).toUpperCase() + evaluation.status.slice(1)}
            </span>
          </div>
        </div>

        {evaluation.ratingMap && Object.keys(evaluation.ratingMap).length > 0 && (
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">Assessment Breakdown</h3>
            <div className="space-y-4">
              {groupedCriteria.map((group) => {
                const groupPoints = group.points.filter(p => evaluation.ratingMap?.[p.id] !== undefined);
                if (groupPoints.length === 0) return null;
                return (
                  <div key={group.category} className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="bg-blue-50 px-4 py-2 border-b border-gray-200">
                      <h4 className="text-xs font-semibold text-blue-900">{group.category}</h4>
                    </div>
                    <div className="divide-y divide-gray-100">
                      {groupPoints.map((point) => {
                        const score = evaluation.ratingMap![point.id];
                        const degreeLabel = DEGREE_LEVELS.find(d => d.value === score);
                        return (
                          <div key={point.id} className="px-4 py-2 flex items-center justify-between">
                            <span className="text-sm text-gray-700">{point.name}</span>
                            {degreeLabel && (
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${degreeLabel.color}`}>
                                {degreeLabel.label}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {evaluation.notes && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
            <p className="text-gray-900 bg-gray-50 p-4 rounded-lg">{evaluation.notes}</p>
          </div>
        )}

        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onPrintAssessmentSheet}
            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 flex items-center space-x-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
            </svg>
            <span>Print Assessment Sheet</span>
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default EvaluationDetails;
